import React, { useRef, useEffect } from "react";
import { Undo2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import AudioPostPlayer from "./AudioPostPlayer";

/**
 * MediaPreviewPanel — shows the captured/uploaded media with
 * retake / undo / confirm controls.
 */
const MediaPreviewPanel = ({
	capturedImage,
	capturedVideo,
	capturedAudio,
	imageToCrop,
	isUploadedMedia,
	mode,
	confirmButtonLabel,
	onRetake,
	onUndo,
	onConfirm,
}) => {
	const previewVideoRef = useRef(null);

	// Ensure preview video plays when captured
	useEffect(() => {
		if (!capturedVideo || !previewVideoRef.current) {
			return;
		}
		const video = previewVideoRef.current;
		video.srcObject = null;
		video.muted = true;

		const initTimeout = setTimeout(() => {
			if (!video.src || video.src !== capturedVideo) {
				video.src = capturedVideo;
			}
			if (video.readyState === 0) {
				video.load();
			}
		}, 300);

		let hasStartedPlaying = false;

		const tryPlay = async () => {
			if (hasStartedPlaying) {
				return;
			}
			try {
				if (video.readyState >= 4 && video.paused) {
					video.currentTime = 0;
					video.muted = true;
					await video.play();
					hasStartedPlaying = true;
				}
			} catch {
				// Controls visible — user can play manually
			}
		};

		const handleCanPlayThrough = () => {
			video.muted = true;
			if (!hasStartedPlaying) {
				tryPlay();
			}
		};

		video.addEventListener("canplaythrough", handleCanPlayThrough, {
			once: true,
		});

		const fallback = setTimeout(() => {
			if (
				video.src &&
				video.readyState >= 4 &&
				video.paused &&
				!hasStartedPlaying
			) {
				tryPlay();
			} else if (video.src && video.readyState === 0) {
				video.load();
			}
		}, 1000);

		return () => {
			clearTimeout(initTimeout);
			clearTimeout(fallback);
			hasStartedPlaying = false;
			video.removeEventListener("canplaythrough", handleCanPlayThrough);
		};
	}, [capturedVideo]);

	if (!capturedImage && !capturedVideo && !capturedAudio) {
		return null;
	}

	// Derive the label from WHAT was captured rather than the current mode —
	// the user can toggle the mode picker after capturing, and we don't want
	// the label to flip to "Use Audio" while showing an image preview.
	const defaultLabel = capturedImage
		? "Use Photo"
		: capturedVideo
			? "Use Video"
			: capturedAudio
				? "Use Audio"
				: mode === "photo"
					? "Use Photo"
					: mode === "video"
						? "Use Video"
						: mode === "audio"
							? "Use Audio"
							: "Use Media";

	const label =
		confirmButtonLabel !== null && confirmButtonLabel !== ""
			? confirmButtonLabel
			: defaultLabel;

	return (
		<>
			{/* Action buttons — top area */}
			<div className="absolute left-0 right-0 top-16 z-10 flex items-center justify-center gap-2 p-2 sm:gap-4 sm:p-4">
				<div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
					{capturedImage && imageToCrop ? (
						<Button
							onClick={onUndo}
							size="lg"
							variant="outline"
							className="border-white/30 bg-white/10 px-4 text-sm text-white backdrop-blur-sm hover:bg-white/20 sm:px-8 sm:text-base"
							title="Undo crop and return to crop screen"
						>
							<Undo2 size={20} className="mr-1 sm:mr-2" />
							Undo
						</Button>
					) : null}
					<Button
						onClick={onRetake}
						size="lg"
						variant="outline"
						className="border-white/30 bg-white/10 px-4 text-sm text-white backdrop-blur-sm hover:bg-white/20 sm:px-8 sm:text-base"
					>
						{isUploadedMedia ? "Choose Different" : "Retake"}
					</Button>
					<Button
						onClick={onConfirm}
						size="lg"
						className="bg-green-500 px-4 text-sm text-white hover:bg-green-600 sm:px-8 sm:text-base"
					>
						<CheckCircle size={20} className="mr-1 sm:mr-2" />
						{label}
					</Button>
				</div>
			</div>

			{/* Media content */}
			{capturedImage ? (
				<div className="flex h-full w-full items-center justify-center pt-24">
					<img
						src={capturedImage}
						alt="Captured"
						style={{
							maxWidth: "90vw",
							maxHeight: "calc(70vh - 6rem)",
							width: "auto",
							height: "auto",
							objectFit: "contain",
						}}
					/>
				</div>
			) : null}

			{capturedVideo ? (
				<div className="relative flex h-full w-full items-center justify-center bg-black">
					<div className="relative h-full w-full md:mx-auto md:w-[calc(100vh*9/16)] md:max-w-[calc(100vw-2rem)]">
						<video
							key={capturedVideo}
							ref={previewVideoRef}
							src={capturedVideo}
							controls
							autoPlay
							muted
							playsInline
							loop
							preload="auto"
							className="h-full w-full object-contain"
							style={{ outline: "none" }}
						>
							Your browser does not support the video tag.
						</video>
						<div className="pointer-events-none absolute bottom-20 left-1/2 -translate-x-1/2 transform rounded-md border border-white/20 bg-black/60 px-3 py-1 text-xs text-white backdrop-blur-sm">
							Video Preview (Looping)
						</div>
					</div>
				</div>
			) : null}

			{capturedAudio ? (
				<div className="relative flex h-full w-full flex-col items-center justify-center gap-8 bg-gradient-to-b from-black via-gray-900 to-black p-8">
					<p className="text-xl font-bold text-white">Audio Preview</p>
					<div className="w-full max-w-md">
						<AudioPostPlayer
							audioUrl={capturedAudio}
							loadImmediately
							showStaticWaveform
						/>
					</div>
					<p className="max-w-sm text-center text-sm text-gray-400">
						Press play to listen to your audio
					</p>
				</div>
			) : null}
		</>
	);
};

export default MediaPreviewPanel;
